import { useEffect, useMemo, useState } from 'react';
import { Comic } from '../../../Models/Comic';
import { Product } from '../../../Models/Product';
import { isProductInProcessingBooks, isTheChapterInCartByVolume, isTheChapterPurchased } from '../../../utils/utils';
import { useAtom, useAtomValue } from 'jotai';
import {
    IsMyBooksFetched,
    MyBooksAtom,
    MyProcessingBooksAtom,
} from '../../../store/MyBooksStore';
import { AccountInfoAtom, LoginStatus, LoginStatusAtom } from '../../../store/AccountStore';
import { Cart } from '../../../Models/Cart';
import { CartAtom } from '../../../store/CartStore';
import Log from '../../Services/Logger';

export const usePurchasedType = (chapter: Comic | Product, productID): any => {
    const [unlockText, setUnlockText] = useState('');
    const [isMyBooksFetched] = useAtom(IsMyBooksFetched);
    const [AccountInfo] = useAtom(AccountInfoAtom);
    const [myBooks] = useAtom(MyBooksAtom);
    const myProcessingBooks = useAtomValue(MyProcessingBooksAtom);

    const login = useAtomValue(LoginStatusAtom);

    const cart = useAtomValue<Cart | false>(CartAtom);

    // const { isComingSoon, isPreOrder, priceType, isOnSale } = chapter?.variants[0];
    const variants = {
        isComingSoon: undefined,
        isPreOrder: false,
        priceType: 'NotForSale',
        isOnSale: undefined, ...chapter?.variants[0],
    };
    const {
        isComingSoon,
        isPreOrder,
        priceType,
        isOnSale,
    } = variants;

    const isChapterInCart = useMemo(() => isTheChapterInCartByVolume(cart, chapter, productID), [cart, chapter, productID]);
    const isChapterPurchased = useMemo(() => isTheChapterPurchased(myBooks, chapter, productID), [myBooks, chapter, productID]);
    const isFree = useMemo(() => priceType === 'Free', [priceType]);
    const isFreeOnRegister = useMemo(() => priceType === 'FreeForRegistered', [priceType]);
    const isNotForSale = useMemo(() => priceType === 'NotForSale', [priceType]);
    const isLoggedIn = useMemo(() => login === "LoggedIn", [login]);
    const isProcessing = useMemo(() =>
            (chapter as any)?.subCategory === 'Chapter' ?
                isProductInProcessingBooks(myProcessingBooks, chapter?.id) || isProductInProcessingBooks(myProcessingBooks, productID) : isProductInProcessingBooks(myProcessingBooks, chapter?.id)
        , [myProcessingBooks, productID]);

    useEffect(() => {
        console.log('myProcessingBooks', myProcessingBooks);
    }, [myProcessingBooks]);

    useEffect(() => {
        if (isLoggedIn) {
            // If the user is logged in we need to wait until the books is being fetched
            if (!isMyBooksFetched || !AccountInfo.email) {
                return;
            }
        }

        if (isFree) {
            setUnlockText('Read');
            return;
        }

        if (isFreeOnRegister && !isLoggedIn) {
            setUnlockText('FREE WITH ACCOUNT');
            return;
        }

        if (isFreeOnRegister && isLoggedIn) {
            setUnlockText('Read');
            return;
        }

        if (isProcessing) {
            setUnlockText('Processing');
            return;
        }

        if (isChapterPurchased && (isComingSoon || isPreOrder)) {
            setUnlockText('Coming Soon');
            return;
        }

        if (isChapterPurchased && isNotForSale) {
            setUnlockText('Read');
            return;
        }

        if (isNotForSale) {
            // setUnlockText('Coming Soon');
            return;
        }

        if (isChapterInCart) {
            setUnlockText('In Cart');
            return;
        }

        if (isChapterPurchased && !isComingSoon) {
            setUnlockText('Read');
            return;
        }

        if (isChapterPurchased && isComingSoon) {
            setUnlockText('Coming Soon');
            return;
        }

        if (isChapterPurchased) {
            setUnlockText('Read');
            return;
        }

        if (isPreOrder) {
            setUnlockText('Pre-order');
            return;
        }

        if (priceType === 'Paid' && isChapterPurchased) {
            setUnlockText('Read');
            return;
        }

        if (priceType === 'Paid' && !isComingSoon) {
            setUnlockText('Buy Digital');
            return;
        }

        if (priceType === 'Paid' && isComingSoon) {
            setUnlockText('Coming Soon');
            return;
        }

    }, [isLoggedIn, myBooks, AccountInfo, isProcessing, isMyBooksFetched, cart, isLoggedIn, isComingSoon, isPreOrder, priceType, isOnSale]);

    Log.debug('chapter', {unlockText, chapterNumber:chapter?.chapterNumber, priceType});
    Log.debug('types', { isProcessing, isComingSoon, isPreOrder, priceType, isOnSale, isNotForSale });


    return {
        unlockText,
        isChapterInCart,
        isChapterPurchased,
        isFree,
        isFreeOnRegister,
        isNotForSale,
        isLoggedIn,
        isComingSoon,
        isProcessing,
        isPreOrder, priceType, isOnSale,
    };
};